// "Master Your Misses" — slim, secondary review bar (sits below Daily Focus,
// which already blends reviews into the daily set). Self-hides when nothing's
// due (also the flag-off case).
//
// Framing (prod 2026-06-06): the raw backlog grows faster than it drains for
// engaged students, so it can never be the win. The HEADER is a bounded daily
// goal + a "fixed today" counter that only goes up — that's what rewards effort.
// Per-subject chips still carry the real count (not a frozen "10+") so a review
// session visibly shrinks them.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../services/api';
import { reviewAPI, ReviewSummary, shortSubject } from '../services/reviewAPI';
import './MasterYourMisses.css';

const MasterYourMisses: React.FC = () => {
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    reviewAPI
      .summary()
      .then((s) => {
        if (cancelled) return;
        setSummary(s);
        if (s && s.total_due > 0) {
          track('review.bar.shown', {
            total_due: s.total_due,
            fixed_today: s.fixed_today || 0,
            mastered: s.mistakes_mastered || 0,
            subjects: s.by_subject.length,
          });
        }
      })
      .catch(() => !cancelled && setSummary(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!summary || summary.total_due <= 0) return null;

  const fixed = summary.fixed_today || 0;
  const target = summary.daily_target || 10;
  const mastered = summary.mistakes_mastered || 0;
  const done = fixed >= target;
  const pct = Math.min(100, Math.round((fixed / target) * 100));

  return (
    <div className="mym-bar">
      <div className="mym-bar-head">
        <span className="mym-bar-icon" aria-hidden>
          🔁
        </span>
        <span className="mym-bar-title">Master your misses</span>
        <span className="mym-bar-progress">
          {done ? '✅ Done today!' : `🔥 ${fixed}/${target} today`}
        </span>
      </div>

      <div className="mym-bar-track" aria-hidden>
        <div className="mym-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="mym-bar-row">
        <div className="mym-bar-chips">
          {summary.by_subject.map((s) => (
            <button
              key={s.subject}
              className="mym-bar-chip"
              onClick={() => {
                track('review.subject.clicked', {
                  subject: s.subject,
                  due_count: s.due_count,
                });
                navigate(`/review/misses/${encodeURIComponent(s.subject)}`);
              }}
            >
              {shortSubject(s.subject)}
            </button>
          ))}
        </div>
        {mastered > 0 && (
          <span className="mym-bar-foot">✨ {mastered} all-time</span>
        )}
      </div>
    </div>
  );
};

export default MasterYourMisses;
